import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import client from "../../api/client";

const inputCls = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30";
const MANUAL = "e1x";
const MANUAL_LIFETIME_DAYS = 30;

const inDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

function timeAgo(iso) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

function JobModal({ job, options, onClose, onSaved }) {
  const isEdit = !!job;
  const [form, setForm] = useState({
    title: job?.title || "",
    company: job?.company || "",
    location: job?.location || "",
    region: job?.region || "nigeria",
    remote_type: job?.remote_type || "onsite",
    employment_type: job?.employment_type || "full_time",
    seniority: job?.seniority || "",
    career_paths: job?.category ? job.category.split(",").map((c) => c.trim()) : [],
    description: job?.description || "",
    salary_min: job?.salary_min ?? "",
    salary_max: job?.salary_max ?? "",
    salary_currency: job?.salary_currency || "NGN",
    apply_link: (job?.source_url || "").replace(/^mailto:/i, ""),
    expires_at: job?.expires_at?.slice(0, 10) || inDays(MANUAL_LIFETIME_DAYS),
    is_active: job?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  // One month from the original posting date — editing doesn't restart it.
  const latestClosing = (() => {
    const d = job?.posted_at ? new Date(job.posted_at) : new Date();
    d.setDate(d.getDate() + MANUAL_LIFETIME_DAYS);
    return d.toISOString().slice(0, 10);
  })();
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const togglePath = (path) =>
    setForm((f) => ({
      ...f,
      career_paths: f.career_paths.includes(path) ? f.career_paths.filter((p) => p !== path) : [...f.career_paths, path],
    }));

  const canSave = form.title.trim() && form.company.trim() && form.description.trim()
    && form.apply_link.trim() && form.career_paths.length > 0 && form.expires_at;

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      seniority: form.seniority || null,
      salary_min: form.salary_min === "" ? null : form.salary_min,
      salary_max: form.salary_max === "" ? null : form.salary_max,
    };
    try {
      const res = isEdit
        ? await client.put(`/admin/job-postings/${job.id}`, payload)
        : await client.post("/admin/job-postings", payload);
      toast.success(res.data?.message || "Saved.");
      onSaved();
    } catch (e) {
      const errors = e?.response?.data?.errors;
      toast.error(errors ? Object.values(errors)[0][0] : e?.response?.data?.message || "Failed to save.");
    } finally { setSaving(false); }
  };

  const select = (key, entries, placeholder) => (
    <select value={form[key]} onChange={set(key)} className={inputCls}>
      {placeholder && <option value="">{placeholder}</option>}
      {Object.entries(entries).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
    </select>
  );

  const label = (text) => <span className="block text-xs font-semibold text-gray-500 mb-1">{text}</span>;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-gray-900 mb-5">{isEdit ? "Edit job" : "Add a job"}</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label>{label("Job title *")}<input value={form.title} onChange={set("title")} className={inputCls} /></label>
            <label>{label("Company *")}<input value={form.company} onChange={set("company")} className={inputCls} /></label>
            <label>{label("Location")}<input value={form.location} onChange={set("location")} placeholder="e.g. Lagos, Nigeria" className={inputCls} /></label>
            <label>{label("Region *")}{select("region", options.regions)}</label>
            <label>{label("Work style *")}{select("remote_type", options.remote_types)}</label>
            <label>{label("Job type")}{select("employment_type", options.employment_types, "Not specified")}</label>
            <label>{label("Level")}{select("seniority", options.seniorities, "Any level")}</label>
            <label>{label("Closing date *")}<input type="date" min={inDays(0)} max={latestClosing} value={form.expires_at} onChange={set("expires_at")} className={inputCls} />
              <span className="block text-[11px] text-gray-400 mt-1">Comes down after {MANUAL_LIFETIME_DAYS} days at most.</span></label>
          </div>

          <div>
            {label("Show to students on these career paths *")}
            <div className="flex flex-wrap gap-2">
              {options.career_paths.map((path) => {
                const on = form.career_paths.includes(path);
                return (
                  <button key={path} type="button" onClick={() => togglePath(path)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      on ? "bg-[#294a93] text-white border-[#294a93]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                    }`}>
                    {path}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="block">{label("Description *")}
            <textarea rows={7} value={form.description} onChange={set("description")}
              placeholder="Responsibilities, requirements, how to apply…" className={`${inputCls} resize-y`} />
          </label>

          <div className="grid grid-cols-3 gap-3">
            <label>{label("Salary from")}<input type="number" min="0" value={form.salary_min} onChange={set("salary_min")} className={inputCls} /></label>
            <label>{label("Salary to")}<input type="number" min="0" value={form.salary_max} onChange={set("salary_max")} className={inputCls} /></label>
            <label>{label("Currency")}<input value={form.salary_currency} onChange={set("salary_currency")} className={inputCls} /></label>
          </div>

          <label className="block">{label("Apply link or email *")}
            <input value={form.apply_link} onChange={set("apply_link")} placeholder="https://… or careers@company.com" className={inputCls} />
          </label>

          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} className="rounded" />
            Visible to students
          </label>
        </div>
        <div className="flex gap-3 mt-6 justify-end">
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving || !canSave}
            className="px-5 py-2.5 rounded-xl bg-[#294a93] text-white text-sm font-semibold disabled:opacity-50 hover:bg-[#1e3a7a] transition-colors">
            {saving ? "Saving…" : isEdit ? "Save" : "Post job"}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatCountdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${h}h ${pad(m)}m ${pad(s)}s`;
}

function useNow(active) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

function JobBoardStatus({ status, onRefresh, onCooldownOver }) {
  const lastPull = status.last_pull;
  const availableAt = status.refresh_available_at ? new Date(status.refresh_available_at).getTime() : null;
  const now = useNow(!!availableAt);
  const remaining = availableAt ? availableAt - now : 0;
  const coolingDown = remaining > 0;

  // Re-enable without a page reload the moment the countdown ends.
  useEffect(() => {
    if (availableAt && remaining <= 0) onCooldownOver();
  }, [availableAt, remaining, onCooldownOver]);

  const stat = (labelText, value) => (
    <div>
      <p className="text-xs text-gray-400">{labelText}</p>
      <p className="text-sm font-semibold text-gray-800">{value}</p>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-3">
          {stat("Newest job added", status.last_new_job_at ? timeAgo(status.last_new_job_at) : "Never")}
          {stat("Last update", lastPull ? `${timeAgo(lastPull.finished_at)} · ${lastPull.new} new` : "Never")}
          {stat("Next scheduled update", new Date(status.next_scheduled_at).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }))}
        </div>
        <div className="text-right">
          <button onClick={onRefresh} disabled={status.refreshing || coolingDown}
            className="px-4 py-2 rounded-xl bg-[#294a93] text-white text-sm font-semibold hover:bg-[#1e3a7a] disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors">
            {status.refreshing ? "Updating jobs…" : coolingDown ? `Available in ${formatCountdown(remaining)}` : "Update jobs now"}
          </button>
          <p className="text-[11px] text-gray-400 mt-1.5">
            Manual updates: once every {status.cooldown_hours} hours
            {status.refresh_last_used && ` · last used ${timeAgo(status.refresh_last_used)}`}
          </p>
        </div>
      </div>
      {lastPull?.sources && (
        <div className="flex flex-wrap gap-2 mt-4">
          {Object.entries(lastPull.sources).map(([name, r]) => (
            <span key={name} title={r.error || ""}
              className={`text-xs px-2 py-0.5 rounded-full ${r.ok ? (r.count > 0 ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500") : "bg-red-50 text-red-600"}`}>
              {name}: {r.ok ? r.count : "failed"}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminJobsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", source: "", region: "", status: "live" });
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null); // null | "new" | posting
  const [busyId, setBusyId] = useState(null);

  const load = useCallback((p = 1) => {
    setLoading(true);
    client.get("/admin/job-postings", { params: { ...filters, page: p } })
      .then((res) => { setData(res.data); setPage(p); })
      .catch(() => toast.error("Failed to load jobs."))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(() => load(1), 300);
    return () => clearTimeout(t);
  }, [load]);

  const setStatus = (status) => status && setData((d) => d && { ...d, status });

  const refresh = async () => {
    if (!window.confirm("Update jobs from every source now? This can only be done once every 48 hours.")) return;
    try {
      const res = await client.post("/admin/job-postings/refresh");
      toast.success(res.data?.message);
      setStatus(res.data?.status);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not start an update.");
      setStatus(e?.response?.data?.status);
    }
  };

  const cooldownOver = useCallback(() => {
    setData((d) => d && { ...d, status: { ...d.status, refresh_available_at: null } });
  }, []);

  const toggleActive = async (job) => {
    setBusyId(job.id);
    try {
      await client.put(`/admin/job-postings/${job.id}`, { is_active: !job.is_active });
      load(page);
    } catch { toast.error("Update failed."); }
    finally { setBusyId(null); }
  };

  const remove = async (job) => {
    if (!window.confirm(`Delete "${job.title}"?`)) return;
    setBusyId(job.id);
    try {
      await client.delete(`/admin/job-postings/${job.id}`);
      toast.success("Deleted.");
      load(page);
    } catch (e) { toast.error(e?.response?.data?.message || "Delete failed."); }
    finally { setBusyId(null); }
  };

  const options = data?.options;
  const postings = data?.postings;
  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const filterCls = "border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30";

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Job Board</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {data ? `${data.summary.live} live jobs · ${data.summary.live_africa} in Nigeria & Africa · ${data.summary.manual} posted by E1X` : "Jobs pulled from job boards plus jobs you post."}
          </p>
        </div>
        <button onClick={() => setModal("new")} disabled={!options}
          className="px-4 py-2.5 rounded-xl bg-[#294a93] text-white text-sm font-semibold hover:bg-[#1e3a7a] transition-colors disabled:opacity-50">
          + Add job
        </button>
      </div>

      {data?.status && <JobBoardStatus status={data.status} onRefresh={refresh} onCooldownOver={cooldownOver} />}

      <div className="flex items-center gap-3 flex-wrap mb-5">
        <input value={filters.search} onChange={setFilter("search")} placeholder="Search title, company, location…" className={`${filterCls} w-64`} />
        <select value={filters.status} onChange={setFilter("status")} className={filterCls}>
          <option value="live">Live</option>
          <option value="hidden">Hidden</option>
          <option value="">All</option>
        </select>
        <select value={filters.source} onChange={setFilter("source")} className={filterCls}>
          <option value="">All sources</option>
          {options && Object.entries(options.sources).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.region} onChange={setFilter("region")} className={filterCls}>
          <option value="">All regions</option>
          {options && Object.entries(options.regions).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {loading && !postings ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : !postings?.data?.length ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-sm text-gray-500">No jobs match these filters.</div>
      ) : (
        <div className={`space-y-3 ${loading ? "opacity-60" : ""}`}>
          {postings.data.map((job) => {
            const manual = job.source === MANUAL;
            return (
              <div key={job.id} className={`bg-white rounded-xl border border-gray-100 p-4 flex gap-4 items-start ${job.is_active ? "" : "opacity-60"}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${manual ? "bg-orange-50 text-[#d15424]" : "bg-gray-100 text-gray-600"}`}>
                      {options?.sources[job.source] || job.source}
                    </span>
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{options?.regions[job.region] || job.region || "No region"}</span>
                    <span className="text-xs text-gray-400">{job.matches_count} matched</span>
                  </div>
                  <a href={job.source_url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-gray-900 hover:text-[#294a93]">{job.title}</a>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {[job.company, job.location].filter(Boolean).join(" · ")}
                    {job.posted_at && ` · posted ${job.posted_at.slice(0, 10)}`}
                    {job.expires_at && ` · closes ${job.expires_at.slice(0, 10)}`}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                  <button onClick={() => toggleActive(job)} disabled={busyId === job.id}
                    className="text-xs font-semibold text-gray-600 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
                    {job.is_active ? "Hide" : "Show"}
                  </button>
                  {manual && (
                    <>
                      <button onClick={() => setModal(job)}
                        className="text-xs font-semibold text-[#294a93] px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">Edit</button>
                      <button onClick={() => remove(job)} disabled={busyId === job.id}
                        className="text-xs font-semibold text-red-600 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-red-50 disabled:opacity-50">Delete</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {postings && postings.last_page > 1 && (
        <div className="flex items-center justify-between mt-5 text-sm text-gray-500">
          <span>Page {postings.current_page} of {postings.last_page} · {postings.total} jobs</span>
          <div className="flex gap-2">
            <button onClick={() => load(page - 1)} disabled={page <= 1 || loading}
              className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40">Previous</button>
            <button onClick={() => load(page + 1)} disabled={page >= postings.last_page || loading}
              className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

      {modal && options && (
        <JobModal
          job={modal === "new" ? null : modal}
          options={options}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(page); }}
        />
      )}
    </div>
  );
}
